import { useFormContext } from 'react-hook-form';
import { PropsWithChildren } from 'react';
import { LocalizedProperty, lt, useFeatureFlag } from '@mews/framework';
import { Banner, Card, Stack, Typography } from '@optimus-web/core';
import { Loader, LocalizedDateFromUtc, SpacedDash } from '@mews-commander/ui';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import {
    PolicyExceptionsDatesFieldName,
    PaymentPolicyFieldName,
    FieldSectionName,
    SettlementConfigurationsFieldName,
} from '../../types';
import { CancellationPolicyName, SettlementRuleName } from '../../../../../../components';
import { PolicyExceptionFormValues } from '../../../../schemas';
import {
    useGetSelectedCancellationPolicies,
    useGetSelectedRates,
    useGetSelectedSettlementRules,
} from '../../../../queries';
import { useCurrentRateGroupContext } from '../../../../contexts';
import { IncompatibleIntegrationsBanner } from '../../../../../../components/common/IncompatibleIntegrationsBanner';

export const PolicyExceptionsFormSummaryContent = () => {
    const { watch } = useFormContext<PolicyExceptionFormValues>();

    const selectedRates = watch(FieldSectionName.Rates);
    const selectedCancellationFeeIds = watch(PaymentPolicyFieldName.CancellationFees);
    const selectedSettlementRuleIds = watch(PaymentPolicyFieldName.SettlementRules);
    const requirePaymentDuringOCIField = watch(SettlementConfigurationsFieldName.RequirePaymentCardDuringOnlineCheckIn);

    const selectedTimeIntervals = watch(
        `${FieldSectionName.Dates}.${PolicyExceptionsDatesFieldName.TimeUnitIntervals}`
    ) as PolicyExceptionFormValues['dates']['timeUnitIntervals'];

    const cancellationFeesQuery = useGetSelectedCancellationPolicies(selectedCancellationFeeIds);
    const settlementRulesQuery = useGetSelectedSettlementRules(selectedSettlementRuleIds);
    const ratesQuery = useGetSelectedRates(selectedRates.exceptionType === 'rate' ? selectedRates.rates : null);

    const requirePaymentDuringOCI = useFeatureFlag(FeatureFlag.requirePaymentDuringOnlineCheckIn);

    const { names } = useCurrentRateGroupContext();

    if (settlementRulesQuery.isFetching || cancellationFeesQuery.isFetching || ratesQuery.isFetching) {
        return <Loader />;
    }

    return (
        <>
            {settlementRulesQuery.isSuccess && cancellationFeesQuery.isSuccess && ratesQuery.isSuccess && (
                <Stack vertical spacing="200">
                    <Card padding="300">
                        <Stack vertical spacing="200">
                            <Typography textStyle="titleMediumStrong" as="div">
                                {lt('Summary')}
                            </Typography>

                            <SummarySection title={TextKey.PolicyExceptionFormDatesTitle}>
                                {selectedTimeIntervals.map(({ firstTimeUnitStartUtc, lastTimeUnitStartUtc }) => (
                                    <Typography
                                        key={`${firstTimeUnitStartUtc}-${lastTimeUnitStartUtc}`}
                                        textStyle="bodyMedium"
                                        variant="secondary"
                                        as="div"
                                    >
                                        <LocalizedDateFromUtc utcString={firstTimeUnitStartUtc} />
                                        {SpacedDash}
                                        <LocalizedDateFromUtc utcString={lastTimeUnitStartUtc} />
                                    </Typography>
                                ))}
                            </SummarySection>

                            {selectedRates.exceptionType === 'rateGroup' ? (
                                <SummarySection title={TextKey.RateGroup}>
                                    <Typography textStyle="bodyMedium" variant="secondary" as="div">
                                        <LocalizedProperty property={names} />
                                    </Typography>
                                </SummarySection>
                            ) : (
                                <SummarySection title={TextKey.RatePlural}>
                                    {ratesQuery.data.map(({ name, id }) => (
                                        <Typography textStyle="bodyMedium" variant="secondary" key={id} as="div">
                                            {name}
                                        </Typography>
                                    ))}
                                </SummarySection>
                            )}

                            <SummarySection title={TextKey.Payment}>
                                {settlementRulesQuery.data.map((settlementRule) => (
                                    <Typography
                                        textStyle="bodyMedium"
                                        variant="secondary"
                                        key={settlementRule.id}
                                        as="div"
                                    >
                                        <SettlementRuleName {...settlementRule} format="nameAndParameters" />
                                    </Typography>
                                ))}
                            </SummarySection>

                            <SummarySection title={TextKey.Cancellation}>
                                {cancellationFeesQuery.data.map((cancellationFee) => (
                                    <Typography
                                        textStyle="bodyMedium"
                                        variant="secondary"
                                        key={cancellationFee.id}
                                        as="div"
                                    >
                                        <CancellationPolicyName {...cancellationFee} format="nameAndParameters" />
                                    </Typography>
                                ))}
                            </SummarySection>

                            {requirePaymentDuringOCI && (
                                <SummarySection title={TextKey.CheckInRequirePaymentDetails}>
                                    <Typography textStyle="bodyMedium" variant="secondary" as="div">
                                        {requirePaymentDuringOCIField ? lt('Yes') : lt('No')}
                                    </Typography>
                                </SummarySection>
                            )}
                        </Stack>
                    </Card>
                    <Banner variant="info" primaryText={lt('RateGroupPolicyExceptionsSummaryBannerTitle')} />
                    <IncompatibleIntegrationsBanner />
                </Stack>
            )}
        </>
    );
};

const SummarySection = ({ children, title }: PropsWithChildren<{ title: Mews.TextKey }>) => (
    <Stack vertical spacing="100">
        <Typography textStyle="bodyMediumStrong" as="div">
            {lt(title)}
        </Typography>
        <Stack vertical>{children}</Stack>
    </Stack>
);
